import { useEffect, useState, type JSX } from 'react';
import { fetchOperatorPortalMe, type OperatorPortalMe } from './api';
import { useAuth } from './auth/useAuth';
import { ComplianceSection } from './operator-portal/ComplianceSection';
import { FleetSection } from './operator-portal/FleetSection';
import { MissionsSection } from './operator-portal/MissionsSection';
import { ProfileSection } from './operator-portal/ProfileSection';
import { RateCardSection } from './operator-portal/RateCardSection';

/**
 * Operator Mode portal (Drone D1 #316 shell → #318 real portal) — the stripped-down
 * surface an EXTERNAL drone-operator principal lands on instead of the COP. Per the
 * founder: Operator Mode is intentionally minimal — only what the operator needs to tell
 * us who they are and manage their fleet. #318 fills it in: onboarding/profile, regulatory
 * compliance (with expiry flags), fleet/asset registration, and the availability toggle.
 *
 * IMPORTANT: this shell is COSMETIC. The actual class boundary is the SERVER gate
 * (`registerCopAccessGate`) — a drone-operator principal is 403'd on every COP route
 * regardless of what the web renders, and every portal call is server-scoped to the
 * operator's own agency. AuthGate routes here when the resolved principal is non-COP
 * (`!roleCanAccessCop(user.role)`); the COP is never mounted for them.
 *
 * On-brand with the cold-boot BootSplash (#259): dark ops-room palette, the "TSR Platform"
 * wordmark, colour-discipline-safe neutral slate (no reserved red/green; urgency in amber).
 */
export function OperatorModeShell(): JSX.Element {
  const { user, logout } = useAuth();
  const [me, setMe] = useState<OperatorPortalMe | null>(null);

  // The header identity comes from the auth context; we also pull the portal /me for the
  // org name in the header. Degrade gracefully — the sections own their own data.
  useEffect(() => {
    const ac = new AbortController();
    let alive = true;
    void fetchOperatorPortalMe(ac.signal)
      .then((m) => {
        if (alive) setMe(m);
      })
      .catch(() => {});
    return () => {
      alive = false;
      ac.abort();
    };
  }, []);

  const displayName = me?.operator.displayName ?? user?.displayName ?? user?.email ?? 'Operator';
  const orgName = me?.organization?.name ?? null;

  return (
    <div
      data-testid="operator-mode-shell"
      className="flex h-full w-full justify-center overflow-y-auto bg-slate-950 px-6 py-10 text-slate-300"
    >
      <div className="flex w-full max-w-2xl flex-col gap-6">
        <header className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-xl font-semibold tracking-tight text-slate-100">TSR Platform</span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Operator Mode</span>
          </div>
          <div className="flex flex-col items-end gap-0.5">
            <span className="text-sm font-medium text-slate-100" data-testid="operator-mode-name">
              {displayName}
            </span>
            {orgName ? (
              <span className="text-xs text-slate-400" data-testid="operator-mode-org">
                {orgName}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => void logout()}
              data-testid="operator-mode-signout"
              className="mt-1 text-xs text-slate-400 underline-offset-2 hover:text-slate-200 hover:underline"
            >
              Sign out
            </button>
          </div>
        </header>

        <ProfileSection />
        <RateCardSection />
        <ComplianceSection />
        <FleetSection />
        <MissionsSection />
      </div>
    </div>
  );
}
